import { createContext, ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { EventTypes } from "../../API/api";
import { useConfig } from '../../config/useConfig';
import { log } from "../../Utilities/logging";
import { usePersistReducer } from "../../Utilities/usePersistReducer";
import { AppAction, appReducer, AppState, getStorageIdentifier, initialState } from "./appReducer";

export const AppContext = createContext<{
    state: AppState;
    dispatch: React.Dispatch<AppAction>;
}>({
    state: initialState,
    dispatch: () => null
});

export const AppProvider = (props: { children: ReactNode }) => {
    const config = useConfig()
    initialState.messages[0]["content"] = config?.WelcomeMessage ?? "Hello, how can I help you today?";
    const [appState, dispatch] = usePersistReducer(initialState, getStorageIdentifier(), appReducer);
    const { pathname } = useLocation();
    useEffect(() => {
        if (pathname !== "/index.html")
            dispatch({ type: "setLastLocation", location: pathname })
    }, [pathname, dispatch])

    // Read pageurl from iframe query params on mount
    useEffect(() => {
        const pageUrl = new URLSearchParams(window.location.search).get('pageurl');
        if (pageUrl) {
            dispatch({ type: 'setPageUrl', pageUrl });
        }
    }, [dispatch]);

    // Listen for parent SPA navigation updates
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            const msg = event.data as { isEdulla?: boolean; action?: string; data?: any };
            if (msg?.isEdulla && msg.action === 'updatePageUrl' && typeof msg.data === 'string') {
                dispatch({ type: 'setPageUrl', pageUrl: msg.data });
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [dispatch]);

    // Page view event tracking: only fires after the user has sent at least
    // one message in the session. Once "armed", every change to pageUrl logs
    // a PageVisit event. This gates event volume to engaged visitors only.
    const hasSentMessage = appState.messages.some((m: { direction: number }) => m.direction === 0);
    useEffect(() => {
        if (!hasSentMessage) return;
        if (!appState.pageUrl) return;
        if (!config?.IndexName) return;

        try {
            api.logEvent.logEvent({
                eventtype: EventTypes.PageVisit.toString(),
                eventvalue: appState.pageUrl,
                indexname: config.IndexName,
                sessionid: appState.sessionid,
                pageurl: appState.pageUrl,
            });
        } catch (e) {
            log("edulla-chat", (e as Error).message, "visitor", { sessionId: appState.sessionid });
        }
    }, [hasSentMessage, appState.pageUrl, config?.IndexName, appState.sessionid]);

    return (
        <AppContext.Provider value={{ state: appState, dispatch }}>
            {props.children}
        </AppContext.Provider>
    );
};
